import { useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

// "Management Finance" (the old manual ledger, /management-finance) and the
// Payroll Generator page are deliberately not listed — their routes still
// exist but they are no longer part of the menu.
const GROUPS = [
  { key: "operations", label: "Operations" },
  { key: "receivables", label: "Receivables & Tax" },
  { key: "reporting", label: "Finance & Reporting" },
  { key: "clients", label: "Clients" }
];

const navItems = [
  { label: "Create Invoice", path: "/create-invoice", group: "operations" },
  { label: "Petty Cash Portal", path: "/petty-cash", group: "operations" },
  { label: "Finance AI Agent", path: "/finance-ai", group: "operations", financeOnly: true },
  { label: "Accounts Receivable", path: "/management-finance/receivables", group: "receivables", managementFinanceOnly: true },
  { label: "Manual Invoice Entry", path: "/management-finance/manual-entry", group: "receivables", managementFinanceOnly: true },
  { label: "WHT & GST Control", path: "/management-finance/wht-gst-control", group: "receivables", managementFinanceOnly: true },
  { label: "Client Tax Profiles", path: "/management-finance/tax-profiles", group: "receivables", managementFinanceOnly: true },
  { label: "Operating Expenses", path: "/management-finance/operating-expenses", group: "reporting", managementFinanceOnly: true },
  { label: "Monthly P&L", path: "/management-finance/monthly-pnl", group: "reporting", managementFinanceOnly: true },
  { label: "Payroll Financing", path: "/management-finance/payroll-financing", group: "reporting", managementFinanceOnly: true },
  { label: "CPBM Fixed-Fee Statement", path: "/management-finance/fixed-fee", group: "clients", managementFinanceOnly: true },
  { label: "GSolar PPE Details", path: "/gsolar-ppe", group: "clients", ppeUsers: ["admin@almhr.com", "laeeq@almhr.com", "nazim@almhr.com", "krashid@gsolar.pk"] }
];

// External client login: only the PPE tab is shown.
const PPE_ONLY_EMAILS = ["krashid@gsolar.pk"];
const STORAGE_KEY = "alm-sidebar-collapsed-groups";

function readCollapsed() {
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]"); } catch (error) { return []; }
}

function writeCollapsed(keys) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys)); } catch (error) { /* storage unavailable — menu just won't remember */ }
}

export default function Sidebar() {
  const currentPath = window.location.pathname;
  const currentEmail = auth.currentUser?.email?.toLowerCase() || "";
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const visibleNavItems = navItems.filter((item) =>
    (PPE_ONLY_EMAILS.includes(currentEmail) ? Boolean(item.ppeUsers) : true)
    && (!item.ppeUsers || item.ppeUsers.includes(currentEmail))
    && (!item.managementFinanceOnly || ["admin@almhr.com", "nazim@almhr.com"].includes(currentEmail))
    && (!item.financeOnly || currentEmail === "nazim@almhr.com")
  );
  const invoicePaths = ["/", "/create-invoice", "/general-invoice", "/cbre-invoice", "/iil-invoice", "/iil-attendance", "/iil-advances", "/corteva-invoice", "/corteva-reconciliation", "/temporary-invoice"];
  const isActive = (item) => currentPath === item.path || (item.path === "/create-invoice" && invoicePaths.includes(currentPath));

  const navigate = (path) => {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const toggle = (key) => {
    const next = collapsed.includes(key) ? collapsed.filter((item) => item !== key) : [...collapsed, key];
    setCollapsed(next);
    writeCollapsed(next);
  };

  const logout = () => {
    signOut(auth);
    window.location.reload();
  };

  return (
    <aside className="sidebar">
      <div className="brand-lockup">
        <img src="/alm-logo.png" alt="ALM logo" className="brand-logo" />
        <h3 className="brand-title">ALM Invoice Engine</h3>
        <p className="brand-subtitle">Payroll and invoice operations</p>
      </div>

      <nav className="nav-list" aria-label="Main navigation">
        {GROUPS.map((group) => {
          const items = visibleNavItems.filter((item) => item.group === group.key);
          if (!items.length) return null;
          // The group holding the current page is always open so you never lose your place.
          const open = !collapsed.includes(group.key) || items.some(isActive);
          return (
            <div className="nav-group" key={group.key}>
              <button type="button" className="nav-group-title" aria-expanded={open} onClick={() => toggle(group.key)}>
                <span>{group.label}</span><span className={`nav-caret ${open ? "open" : ""}`} aria-hidden="true">▾</span>
              </button>
              {open && items.map((item) => (
                <button key={item.path} type="button" onClick={() => navigate(item.path)} className={`nav-item ${isActive(item) ? "active" : ""}`}>
                  {item.label}
                </button>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button type="button" onClick={logout} className="logout-button">
          Sign Out
        </button>
      </div>
    </aside>
  );
}
